import {IProduct} from "../models/IProduct.ts";
import {createContext, ReactNode, useContext, useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient, UseQueryResult} from "@tanstack/react-query";
import axios from "axios";
import {useNavigate} from "react-router-dom";
import SaleContext from "./SaleContext.tsx";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL + '/api'

interface IProductContextProps {
    products: IProduct[]
    loadingProducts: boolean
    errorFetchingProducts: any
    useFetchProduct: (id: string | undefined) => UseQueryResult<IProduct, Error>
    updateProductMutation: any
    mutateProductError: any
    createProductMutation: any
    createProductError: any
    deleteImageMutation: any
    deleteImageError: any
    addImageMutation: any
    addImageError: any
    deleteProductMutation: any
    deleteProductError: any
    currentPage: number
    setCurrentPage: (page: number) => void
    itemsPerPage: number
    setItemsPerPage: (page: number) => void
}

const ProductContext = createContext<IProductContextProps>({} as IProductContextProps)

interface IProductProviderProps {
    children: ReactNode
}

export const ProductProvider = ({children}: IProductProviderProps) => {
    const {sales} = useContext(SaleContext)
    const [products, setProducts] = useState<IProduct[]>([])
    const [mutateProductError, setMutateProductError] = useState<any>()
    const [createProductError, setCreateProductError] = useState<any>()
    const [deleteProductError, setDeleteProductError] = useState<any>(undefined)
    const [deleteImageError, setDeleteImageError] = useState<any>()
    const [addImageError, setAddImageError] = useState<any>()
    const [itemsPerPage, setItemsPerPage] = useState<number>(20)
    const [currentPage, setCurrentPage] = useState(1);
    const queryClient = useQueryClient()
    const navigate = useNavigate()

    const clearTime = (date: Date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const {data: fetchedProducts, isLoading: loadingProducts, error: errorFetchingProducts} = useQuery({
        queryKey: ['products'],
        queryFn: async () => {
            const response = await axios.get(`${VITE_API_BASE_URL}/products`, {
                withCredentials: true
            })
            return response.data
        }
    })

    const useFetchProduct: (id: string | undefined) => UseQueryResult<IProduct, Error> = (id: string | undefined) => {
        return useQuery({
            queryKey: ['product'],
            queryFn: async () => {
                const response = await axios.get(`${VITE_API_BASE_URL}/products/${id}`, {
                    withCredentials: true,
                })
                return response.data
            }
        })
    }

    const updateProductMutation = useMutation({
        mutationFn: async (product: IProduct) =>
            await axios.put(`${VITE_API_BASE_URL}/products/${product._id}`, product, {
                withCredentials: true,
            }),
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({queryKey: ['product']}),
                queryClient.invalidateQueries({queryKey: ['products']}),
            ]);
            setMutateProductError(undefined)
        },
        onError: error => setMutateProductError(error)
    })

    const createProductMutation = useMutation({
        mutationFn: async (product: IProduct) =>
            await axios.post(`${VITE_API_BASE_URL}/products`, product, {
                withCredentials: true,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['products']})
            navigate('/')
            setCreateProductError(undefined)
        },
        onError: error => setCreateProductError(error)
    })

    const deleteProductMutation = useMutation({
        mutationFn: async (productId: string) => {
            (
                await axios.delete(`${VITE_API_BASE_URL}/products/${productId}`, {
                    withCredentials: true,
                })
            )
        },
        onSuccess: () => {
            setDeleteProductError(undefined)
            queryClient.invalidateQueries({queryKey: ['products']})
            setDeleteProductError(undefined)
        },
        onError: error => setDeleteProductError(error)
    })

    const deleteImageMutation = useMutation({
        mutationFn: async (cleanedUrl: string) =>
            await axios.delete(`${VITE_API_BASE_URL}/delete/${cleanedUrl}`, {withCredentials: true}),
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: ['product']}),
        onError: error => setDeleteImageError(error)
    })

    const addImageMutation = useMutation({
        mutationFn: async (imageData) => {
            const response = await axios.post(`${VITE_API_BASE_URL}/upload`, imageData, {
                withCredentials: true,
                headers: {'Content-Type': 'multipart/form-data'},
            })
            return response.data
        },
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: ['product']}),
        onError: error => setAddImageError(error)
    })

    useEffect(() => {
        if (!fetchedProducts) return;

        if (!sales || sales.length === 0) {
            setProducts(fetchedProducts)
            return
        }

        const updatedProducts = fetchedProducts.map((product: IProduct) => {
            let salePrice = undefined;

            sales.forEach(sale => {
                const nowDay = clearTime(new Date());
                const saleStartDay = clearTime(new Date(sale.from));
                const saleEndDay = clearTime(new Date(sale.to));

                const saleIsActive = saleStartDay <= nowDay && saleEndDay >= nowDay;

                if (saleIsActive && sale.items.find((item) => item === product._id)) {
                    salePrice = product.price * (1 - sale.discount);
                }
            });

            return {
                ...product,
                salePrice: salePrice,
            };
        });
        setProducts(updatedProducts);

    }, [sales, fetchedProducts]);

    return (
        <ProductContext.Provider value={{
            products,
            loadingProducts,
            errorFetchingProducts,
            useFetchProduct,
            updateProductMutation,
            mutateProductError,
            createProductMutation,
            createProductError,
            deleteImageMutation,
            deleteImageError,
            addImageMutation,
            addImageError,
            deleteProductMutation,
            deleteProductError,
            currentPage,
            setCurrentPage,
            itemsPerPage,
            setItemsPerPage
        }}>
            {children}
        </ProductContext.Provider>
    )
}

export default ProductContext